/**
 * "Change password" modal — current + new + confirm fields. Wraps
 * `useChangePassword` and reports field-level errors inline.
 */

import { useEffect, useState, type FormEvent } from "react";

import { ApiError } from "../../api/client";
import { useChangePassword } from "../../api/account";
import Modal from "../../components/Modal";

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ChangePasswordModal({
  isOpen,
  onClose,
}: ChangePasswordModalProps) {
  const change = useChangePassword();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setCurrent("");
      setNext("");
      setConfirm("");
      setError(null);
    }
  }, [isOpen]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (next.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    try {
      await change.mutateAsync({
        current_password: current,
        new_password: next,
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Current password is incorrect.");
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Could not change password.");
      }
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change password">
      <form className="account-form" onSubmit={handleSubmit}>
        <label className="form-row">
          <span className="form-row__label">Current password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="form-row__input"
            required
            autoFocus
          />
        </label>
        <label className="form-row">
          <span className="form-row__label">New password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="form-row__input"
            minLength={8}
            required
          />
        </label>
        <label className="form-row">
          <span className="form-row__label">Confirm new password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="form-row__input"
            minLength={8}
            required
          />
        </label>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="form-actions form-actions--inline">
          <button
            type="button"
            className="button button--ghost"
            onClick={onClose}
            disabled={change.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={change.isPending}
          >
            {change.isPending ? "Saving…" : "Update password"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
